import { MARKS } from "@repo/fika-blog/src/nodes"
import type { Editor } from "@repo/fika-blog/src/tiptap/core"
import { type Accessor, createMemo } from "solid-js"
import { createEditorTransaction, useEditorIsActive } from "solid-tiptap"
import { useI18nContext } from "@/i18n/i18n-solid"
import { formatShortcut } from "@/lib/platform"

interface UseFormatToolbarProps {
  editor: Accessor<Editor | undefined>
}

export function useFormatToolbar(props: UseFormatToolbarProps) {
  const { LL } = useI18nContext()

  const selection = createEditorTransaction(
    props.editor,
    editor => editor?.state.selection,
  )

  const hasTextSelection = createMemo(() => !selection()?.empty)

  function match(type: string, attrs?: Record<string, unknown>) {
    return useEditorIsActive(props.editor, () => type, attrs)
  }

  const markValues = createMemo(() => {
    return Object.entries(MARKS)
      .filter(([type]) => match(type)())
      .map(([type]) => type)
  })

  const linkValue = createMemo(() => {
    if (match("link")()) return "link"
    return null
  })

  const isHighlightActive = createMemo(() => match("highlight")())
  const isLinkActive = createMemo(() => match("link")())

  function getMarkLabel(markType: string): string {
    const markMap: Record<string, string> = {
      bold: LL().editor.bold(),
      italic: LL().editor.italic(),
      strike: LL().editor.strikethrough(),
      code: LL().editor.inlineCode(),
      highlight: LL().editor.highlight(),
    }
    return markMap[markType] || markType
  }

  const markShortcuts: Record<string, string> = {
    bold: formatShortcut("B"),
    italic: formatShortcut("I"),
    strike: formatShortcut("Shift+X"),
    code: formatShortcut("E"),
    highlight: formatShortcut("Shift+H"),
  }

  const markOptions = createMemo(() => {
    return Object.values(MARKS)
      .filter(mark => mark.group === "format")
      .map(mark => ({
        value: mark.type,
        iconName: mark.iconName,
        title: getMarkLabel(mark.type),
        shortcut: markShortcuts[mark.type],
      }))
  })

  function markOnChange(values: string[]) {
    const editor = props.editor()
    if (!editor) return

    const formatMarks = Object.values(MARKS)
      .filter(mark => mark.group === "format")
      .map(mark => mark.type)

    for (const mark of formatMarks) {
      if (values.includes(mark)) {
        editor.chain().focus().setMark(mark).run()
      } else {
        editor.chain().focus().unsetMark(mark).run()
      }
    }
  }

  function linkOnChange(value: string | null) {
    const editor = props.editor()
    if (!editor) return

    switch (value) {
      case null:
        editor.chain().focus().extendMarkRange("link").unsetLink().run()
        return
      case "link":
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .setLink({ href: "" })
          .run()
        return
    }
  }

  function toggleList(type: "bulletList" | "orderedList") {
    const editor = props.editor()
    if (!editor) return

    const chain = editor.chain().focus()
    if (type === "bulletList") {
      chain.toggleBulletList().run()
    } else {
      chain.toggleOrderedList().run()
    }
  }

  return {
    selection,
    hasTextSelection,
    markValues,
    linkValue,
    markOptions,
    markOnChange,
    linkOnChange,
    toggleList,
    isHighlightActive,
    isLinkActive,
    getMarkLabel,
  }
}
